/**
 * Matrix panel — asset matrix view with filters.
 * T-81b2: Rows are now grouped by project (projectName × brand × division).
 * Each group row shows all hubs as tags and the total asset count.
 * Clicking a multi-asset group expands it to show individual asset rows.
 */

import { els } from "../dom";
import { getState, subscribe, updateState } from "../state";
import { escapeHtml } from "../utils/escapeHtml";
import { onPanelActivate, getActivePanel } from "../router";
import { renderEmptyState } from "../components/EmptyState";
import {
  normalizeValue,
  unique,
  fillSelect,
  MAX_RENDERED_ROWS,
} from "./shared";
import type { Asset } from "../types/index";

// ── Types ─────────────────────────────────────────────────────

interface MatrixGroup {
  key: string;
  projectName: string;
  brand: string;
  division: string;
  type: string;
  quarter: string;
  hubs: string[];
  assets: Asset[];
}

// ── Init ─────────────────────────────────────────────────────

export function init(): void {
  onPanelActivate("matrix", render);
  subscribe("assets", render);
  subscribe("forecasts", render);
  bindMatrixFilters();
}

// ── Render ───────────────────────────────────────────────────

export function render(): void {
  if (getActivePanel() !== "matrix") return;
  renderMatrixPanel();
}

// ── Filters ──────────────────────────────────────────────────

function bindMatrixFilters(): void {
  for (const el of [
    els.matrixFilterDivision,
    els.matrixFilterBrand,
    els.matrixFilterHub,
    els.matrixFilterQuarter,
    els.matrixFilterStatus,
  ]) {
    el?.addEventListener("change", applyMatrixFiltersAndRender);
  }
}

// ── Panel render ─────────────────────────────────────────────

export function renderMatrixPanel(): void {
  if (!els.matrixTableBody) return;

  const state = getState();
  fillSelect(
    els.matrixFilterDivision,
    unique(state.assets.map((a) => a.division)),
    "All",
  );
  fillSelect(
    els.matrixFilterBrand,
    unique(state.assets.map((a) => a.brand)),
    "All",
  );
  fillSelect(
    els.matrixFilterHub,
    unique(state.assets.map((a) => a.hub)),
    "All",
  );
  fillSelect(
    els.matrixFilterQuarter,
    unique(state.assets.map((a) => a.quarter)),
    "All",
  );

  applyMatrixFiltersAndRender();
}

// ── Grouping ─────────────────────────────────────────────────

function groupByProject(assets: Asset[]): MatrixGroup[] {
  const map = new Map<string, MatrixGroup>();
  for (const asset of assets) {
    const pName = asset.projectName || asset.name || asset.id || "";
    const key = `${pName}\x00${asset.brand || ""}\x00${asset.division || ""}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        projectName: pName,
        brand: asset.brand || "",
        division: asset.division || "",
        type: asset.type || "",
        quarter: asset.quarter || "",
        hubs: [],
        assets: [],
      };
      map.set(key, g);
    }
    const hub = asset.hub || "";
    if (!g.hubs.includes(hub)) g.hubs.push(hub);
    g.assets.push(asset);
  }
  return [...map.values()].sort((a, b) =>
    a.projectName.localeCompare(b.projectName),
  );
}

// ── Expanded state ────────────────────────────────────────────

function getExpanded(): Set<string> {
  const set = getState().expandedProjects;
  return set instanceof Set ? set : new Set<string>();
}

function toggleExpanded(key: string): void {
  const next = new Set(getExpanded());
  if (next.has(key)) next.delete(key);
  else next.add(key);
  updateState({ expandedProjects: next });
}

// ── Row rendering ─────────────────────────────────────────────

function renderGroup(
  g: MatrixGroup,
  forecastedIds: Set<string>,
  expanded: boolean,
): string {
  const canExpand = g.assets.length > 1;
  const caret = canExpand ? (expanded ? "▾" : "▸") : "";
  const hubTags = g.hubs
    .map((h) => `<span class="hub-tag">${escapeHtml(h)}</span>`)
    .join(" ");

  const hasLocal = g.assets.some(
    (a) => normalizeValue(a.initiativeOrigin) === "local",
  );
  const hasPending = g.assets.some(
    (a) =>
      normalizeValue(a.initiativeOrigin || "") !== "local" &&
      !forecastedIds.has(a.id),
  );
  const statusClass = hasLocal
    ? "local"
    : hasPending
      ? "pending"
      : "forecasted";
  const statusLabel = hasLocal
    ? "Local"
    : hasPending
      ? "Pending"
      : "Forecasted";

  const parent = `<tr class="matrix-group-row">
    <td>${canExpand ? `<button class="btn btn-sm btn-ghost" data-matrix-toggle="${escapeHtml(g.key)}" type="button">${caret}</button>` : ""}</td>
    <td>${escapeHtml(g.projectName)}</td>
    <td>${escapeHtml(g.brand)}</td>
    <td>${escapeHtml(g.division)}</td>
    <td class="matrix-hubs-cell">${hubTags}</td>
    <td>${escapeHtml(g.type)}</td>
    <td>${escapeHtml(g.quarter)}</td>
    <td>${g.assets.length}</td>
    <td><span class="status-badge status-${statusClass}">${statusLabel}</span></td>
  </tr>`;

  if (!expanded) return parent;

  const children = g.assets
    .map((asset) => {
      const isLocal = normalizeValue(asset.initiativeOrigin) === "local";
      const isForecasted = forecastedIds.has(asset.id);
      const sc = isLocal ? "local" : isForecasted ? "forecasted" : "pending";
      const sl = isLocal ? "Local" : isForecasted ? "Forecasted" : "Pending";
      return `<tr class="matrix-child-row">
        <td></td>
        <td class="matrix-child-name">↳ ${escapeHtml(asset.productName || asset.name || "")}</td>
        <td></td>
        <td></td>
        <td>${escapeHtml(asset.hub || "")}</td>
        <td>${escapeHtml(asset.type || "")}</td>
        <td>${escapeHtml(asset.quarter || "")}</td>
        <td style="font-family:monospace;font-size:0.8rem">${escapeHtml(asset.globalEAN || "")}</td>
        <td><span class="status-badge status-${sc}">${sl}</span></td>
      </tr>`;
    })
    .join("");

  return parent + children;
}

// ── Apply filters + render ────────────────────────────────────

function applyMatrixFiltersAndRender(): void {
  if (!els.matrixTableBody) return;

  const state = getState();
  const divFilter =
    (els.matrixFilterDivision as HTMLSelectElement)?.value || "";
  const brandFilter =
    (els.matrixFilterBrand as HTMLSelectElement)?.value || "";
  const hubFilter = (els.matrixFilterHub as HTMLSelectElement)?.value || "";
  const quarterFilter =
    (els.matrixFilterQuarter as HTMLSelectElement)?.value || "";
  const statusFilter =
    (els.matrixFilterStatus as HTMLSelectElement)?.value || "";

  const forecastedIds = new Set(state.forecasts.map((f) => f.assetId));

  const filtered = state.assets.filter((asset) => {
    if (divFilter && asset.division !== divFilter) return false;
    if (brandFilter && asset.brand !== brandFilter) return false;
    if (hubFilter && asset.hub !== hubFilter) return false;
    if (quarterFilter && asset.quarter !== quarterFilter) return false;
    if (statusFilter) {
      const isLocal = normalizeValue(asset.initiativeOrigin) === "local";
      const isForecasted = forecastedIds.has(asset.id);
      if (statusFilter === "forecasted" && !isForecasted) return false;
      if (statusFilter === "pending" && (isForecasted || isLocal)) return false;
      if (statusFilter === "local" && !isLocal) return false;
    }
    return true;
  });

  const truncatedAssets = filtered.slice(0, MAX_RENDERED_ROWS);
  const groups = groupByProject(truncatedAssets);
  const expanded = getExpanded();

  const rows = groups.map((g) =>
    renderGroup(g, forecastedIds, expanded.has(g.key)),
  );

  const truncated =
    filtered.length > MAX_RENDERED_ROWS
      ? `<tr><td colspan="9" style="text-align:center;padding:1rem;color:var(--muted)">Showing first ${MAX_RENDERED_ROWS} of ${filtered.length} rows. Use filters to narrow results.</td></tr>`
      : "";

  if (!rows.length) {
    renderEmptyState(els.matrixTableBody, {
      icon: "📋",
      message: "No assets found",
      hint: "Try adjusting your filters above.",
    });
    return;
  }
  els.matrixTableBody.innerHTML = rows.join("") + truncated;

  // Bind toggle handlers (delegated each render — cheap).
  els.matrixTableBody
    .querySelectorAll<HTMLElement>("[data-matrix-toggle]")
    .forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.stopPropagation();
        const key = btn.dataset.matrixToggle || "";
        toggleExpanded(key);
        applyMatrixFiltersAndRender();
      });
    });
}
